import { HttpClient } from '@angular/common/http'
import { inject } from '@angular/core'
import { Observable } from 'rxjs'

import { environment } from '../../environments/environment'

export type BaseType = {
  id: string
}

export type ResponseData<T> = {
  id: string
  type: string
  attributes: Omit<T, 'id'>
}

export type ListResponseMeta = {
  totalCount: number
  totalPages: number
}

export type ListResponse<
  T extends BaseType,
  M extends ListResponseMeta = ListResponseMeta,
> = {
  data: ResponseData<T>[]
  meta: M
}

export type SingleResponse<T extends BaseType> = {
  data: ResponseData<T>
}

const SINGLE_SCALAR_OPS = [
  // 'any', // used to filter on “to many” relationships
  'endswith', // checks if field ends with a string
  'eq', // checks if field is equal to something
  'ge', // checks if field is greater than or equal to something
  'gt', // checks if field is greater than something
  'has', // used to filter on “to one” relationships
  'ilike', // checks if field contains a string (case insensitive)
  'is', // checks if field is a value
  'isnot', // checks if field is not a value
  'like', // checks if field contains a string
  'le', // checks if field is less than or equal to something
  'lt', // checks if field is less than something
  'match', // checks if field matches against a string or pattern
  'ne', // checks if field is not equal to something
  'notilike', // checks if field does not contain a string (case insensitive)
  'notlike', // checks if field does not contain a string
  'startswith', // checks if field starts with a string
] as const
const SINGLE_SCALAR_OP_SET = new Set<string>(SINGLE_SCALAR_OPS)
type SingleScalarOp = (typeof SINGLE_SCALAR_OPS)[number]

export type SingleScalarExpression<Model = unknown> = {
  name: Model extends object ? keyof Model : string
  op: SingleScalarOp
  val: string | number | boolean
}

const LIST_SCALAR_OPS = [
  'between', // used to filter a field between two values
  'in', // checks if field is in a list of values
  'notin', // checks if field is not in a list of values
] as const
const LIST_SCALAR_OP_SET = new Set<string>(LIST_SCALAR_OPS)
type ListScalarOp = (typeof LIST_SCALAR_OPS)[number]
export type ListScalarExpression<Model = unknown> = {
  name: Model extends object ? keyof Model : string
  op: ListScalarOp
  val: (string | number | boolean)[]
}

export type ScalarExpression<Model = unknown> =
  | SingleScalarExpression<Model>
  | ListScalarExpression<Model>

export type LogicalAndExpression<Model = unknown> = {
  and: Expression<Model>[]
}

export type LogicalOrExpression<Model = unknown> = {
  or: Expression<Model>[]
}

export type LogicalNotExpression<Model = unknown> = {
  not: Expression<Model>[]
}

export type LogicalExpression<Model = unknown> =
  | LogicalAndExpression<Model>
  | LogicalOrExpression<Model>
  | LogicalNotExpression<Model>

export type Expression<Model = unknown> =
  | ScalarExpression<Model>
  | LogicalExpression<Model>
export type Sorting<Model = unknown> = {
  property: Model extends object ? keyof Model : string
  direction: 'asc' | 'des'
}

export type ListRequest<Model = unknown> = {
  limit?: number
  page?: number
  filter?: Expression<Model>[]
  sorting?: Sorting<Model>[]
  fields?: Model extends object ? (keyof Model)[] : string[]
}

export interface RepositoryConfig {
  resourceName: string
  defaultSorting?: Sorting[]
  limit?: number
}

export abstract class BaseRepository<Base extends BaseType> {
  protected http: HttpClient = inject<HttpClient>(HttpClient)
  protected baseUrl = environment.apiUrl
  private readonly repositoryConfig: RepositoryConfig

  protected constructor(httpConfig: RepositoryConfig) {
    this.repositoryConfig = httpConfig
  }

  public get url(): string {
    return `${this.baseUrl}${this.repositoryConfig.resourceName}`
  }

  public list(listRequest?: ListRequest<Base>): Observable<ListResponse<Base>> {
    return this.http.get<ListResponse<Base>>(this.url, {
      params: this.getHttpOptionsFromListRequest(listRequest as ListRequest),
    })
  }

  public getById(id: string): Observable<SingleResponse<Base>> {
    return this.http.get<SingleResponse<Base>>(`${this.url}/${id}`)
  }

  public getHttpOptionsFromListRequest(listRequest?: ListRequest): {
    [key: string]: string
  } {
    const options: { [key: string]: string } = {}

    if (listRequest?.page) {
      options['page[number]'] = listRequest.page.toString()
    }
    if (listRequest?.limit) {
      options['page[size]'] = listRequest.limit.toString()
    }
    if (listRequest?.sorting) {
      options.sort = listRequest.sorting
        .map((sort) => {
          return (sort.direction === 'des' ? '-' : '') + sort.property
        })
        .join(',')
    }
    if (listRequest?.filter && listRequest?.filter.length !== 0) {
      options.filter = JSON.stringify(listRequest.filter)
    }

    return options
  }

  public getSearchQuery(
    sword: string,
    fields: (keyof Base)[]
  ): LogicalExpression<Base> {
    return {
      or: fields.map(
        (searchField) =>
          ({
            name: searchField as Base extends object ? keyof Base : string,
            val: `%${sword}%`,
            op: 'ilike',
          }) satisfies SingleScalarExpression<Base>
      ),
    } satisfies LogicalExpression<Base>
  }

  public isSingleScalarExpression(
    expression: Expression
  ): expression is SingleScalarExpression {
    return (
      expression.hasOwnProperty('op') &&
      SINGLE_SCALAR_OP_SET.has((expression as ScalarExpression).op)
    )
  }

  public isListScalarExpression(
    expression: Expression
  ): expression is ListScalarExpression {
    return (
      expression.hasOwnProperty('op') &&
      LIST_SCALAR_OP_SET.has((expression as ScalarExpression).op)
    )
  }

  public isLogicalANdExpression(
    expression: Expression
  ): expression is LogicalAndExpression {
    return expression.hasOwnProperty('and')
  }

  public isLogicalOrExpression(
    expression: Expression
  ): expression is LogicalOrExpression {
    return expression.hasOwnProperty('or')
  }

  public isLogicalNotExpression(
    expression: Expression
  ): expression is LogicalNotExpression {
    return expression.hasOwnProperty('not')
  }
}
